/* Post-kiosk product journey hook — Phase 4 §4.1 split (GC-P1-05). */

import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePostKioskResolution } from '../../checkout/PostKioskResolutionContext.js';
import { isSignedInNavChrome } from '../../hub/customerHubPaths.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { useCustomerAnalytics } from '../../../shared/analytics/CustomerAnalyticsProvider.js';
import { buildTenantShopFallback } from '../../../shared/session/returnTarget.js';
import { postKioskLog } from '../logging.js';

export function usePostKioskProductJourney() {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const resolution = usePostKioskResolution();
  const paymentId = resolution?.paymentId ?? '';
  const salesPointId = useMemo(() => {
    const id = resolution?.salesPointId;
    return typeof id === 'number' && id > 0 ? id : null;
  }, [resolution?.salesPointId]);
  const favoritePromptEnabled = resolution?.transactionStatus === 'COMPLETED';
  const analytics = useCustomerAnalytics();

  useEffect(() => {
    if (resolution !== null) {
      return;
    }
    postKioskLog.warn('Post-kiosk product journey missing resolution', {
      operation: 'productJourney',
      tenantCode});
  }, [resolution, tenantCode]);

  useEffect(() => {
    if (resolution === null || paymentId.length > 0) {
      return;
    }
    postKioskLog.warn('Post-kiosk product journey missing paymentId', {
      operation: 'productJourney',
      tenantCode});
  }, [paymentId.length, resolution, tenantCode]);

  useEffect(() => {
    analytics.track({
      eventName: 'screen_viewed',
      flow: 'RETAIL',
      screenName: 'post_kiosk_product',
      paymentId: paymentId.length > 0 ? paymentId : null});
  }, [analytics, paymentId]);

  const onPostPayContinue = useCallback((): void => {
    // G12/U1 / G5: URL-tenant account/orders only when !R1 && session after /me.
    // Under R1: no URL-B account deep-link — guest-safe catalog fallback.
    if (isSignedInNavChrome({ session })) {
      navigate('/account/orders', { replace: true });
      return;
    }
    navigate(buildTenantShopFallback(tenantCode), { replace: true });
  }, [session, navigate, tenantCode]);

  return {
    t,
    paymentId,
    salesPointId,
    favoritePromptEnabled,
    onPostPayContinue,
    title: t('postKioskProduct.title'),
    subtitle: t('postKioskProduct.subtitle'),
    doneLabel: t('postKioskProduct.done'),
    description: t('postKioskProduct.description')};
}
